import { useRouter as usePagesRouter } from 'next/compat/router';
import { useParams, usePathname } from 'next/navigation';
import { getLocaleFromQuery } from '@utils/gssp/gsspGetLocale';
import { getLocaleFromI18nPath } from '@services/i18nPaths';

/**
 * Hook: useRouterLocale
 *
 * This hook get the locale information from the router's query parameters.
 * It is compatible with both the App Router (`useParams` from `next/navigation`)
 * and the Pages Router (`useRouter` from `next/compat/router`).
 *
 * First tries to detect locale from the i18n translated path (e.g., /locuri-de-munca → 'ro', /jobs → 'en').
 * Then falls back to the [locale] route param from query.
 *
 * @returns {string | undefined} - The locale from the router's query parameters.
 *
 * @example
 * // Using in a component
 * const locale = useRouterLocale();
 * console.log(locale); // Outputs the locale ("ro", "en", "hu")
 */
export const useRouterLocale = () => {
  // Get query parameters from the Pages Router (legacy)
  const pagesRouterParams = usePagesRouter()?.query;

  // Get parameters from the App Router
  const appRouterParams = useParams();

  // Get the current pathname
  const pathname = usePathname();

  // First try to detect locale from the i18n translated path (e.g., /locuri-de-munca → 'ro', /jobs → 'en')
  // Then fall back to the [locale] route param from query
  return (
    getLocaleFromI18nPath(pathname ?? '')
    ?? getLocaleFromQuery(appRouterParams || pagesRouterParams)
  ) as Locale;
};
